import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';
import {
  getOverlayTemplateScopeKey,
  getOverlayTemplate,
  listOverlayTemplateFolders,
  listOverlayTemplates,
  listOverlayTemplatesPage,
  type OverlayTemplateDetailDto,
  type OverlayTemplateOrdering
} from '../../../../api/overlayTemplates';
import { LomavisOverlayTemplateLibrary } from '../../../../lomavisSharedUi/templates/pages/LomavisOverlayTemplateLibrary';
import type {
  OverlayTemplateFolder,
  OverlayTemplateFolderLibrary,
  OverlayTemplateLibrarySelection,
  OverlayTemplateSortValue,
  OverlayTemplateSummary
} from '../../../../lomavisSharedUi/types/overlayTemplates';
import {
  getOverlayTemplateFolderId,
  getOverlayTemplateFolderLibrary
} from '../../../../lomavisSharedUi/types/overlayTemplates';

type Props = {
  show: boolean;
  locationUuid?: string | null;
  onClose: () => void;
  onSelect: (template: OverlayTemplateDetailDto) => void;
};

const TEMPLATE_SELECTOR_MODAL_WIDTH = 'min(90vw, 1600px)';
const TEMPLATE_SELECTOR_BODY_HEIGHT = '75vh';
const TEMPLATE_SEARCH_DEBOUNCE_MS = 250;

const WideModalDialog = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Modal.Dialog>
>(
  // react-bootstrap provides the dialog props through `dialogAs`.
  // eslint-disable-next-line react/prop-types
  ({ style, ...props }, ref) => {
    const dialogStyle: React.CSSProperties & {
      '--bs-modal-width': string;
    } = {
      ...style,
      '--bs-modal-width': TEMPLATE_SELECTOR_MODAL_WIDTH
    };

    return <Modal.Dialog {...props} ref={ref} style={dialogStyle} />;
  }
);

WideModalDialog.displayName = 'WideModalDialog';

const orderingBySort: Record<
  OverlayTemplateSortValue,
  OverlayTemplateOrdering
> = {
  newest: '-pk',
  oldest: 'pk',
  nameAsc: 'title',
  nameDesc: '-title'
};

const OverlayTemplateSelectorModal: React.FC<Props> = ({
  show,
  locationUuid,
  onClose,
  onSelect
}) => {
  const { t } = useTranslate();
  const [folders, setFolders] = useState<OverlayTemplateFolder[]>([]);
  const [templates, setTemplates] = useState<OverlayTemplateSummary[]>([]);
  const [selection, setSelection] =
    useState<OverlayTemplateLibrarySelection>('all');
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] =
    useState<OverlayTemplateSortValue>('newest');
  const [isLoading, setIsLoading] = useState(false);
  const [templateCount, setTemplateCount] = useState(0);
  const [nextTemplatesUrl, setNextTemplatesUrl] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreErrorMessage, setLoadMoreErrorMessage] = useState<
    string | null
  >(null);
  const [isFolderLoading, setIsFolderLoading] = useState(false);
  const [loadedFolderScopeKey, setLoadedFolderScopeKey] = useState<
    string | null
  >(null);
  const [nextFoldersUrl, setNextFoldersUrl] = useState<string | null>(null);
  const [isFolderLoadingMore, setIsFolderLoadingMore] = useState(false);
  const [folderLoadMoreErrorMessage, setFolderLoadMoreErrorMessage] = useState<
    string | null
  >(null);
  const [selectingTemplateId, setSelectingTemplateId] = useState<string | null>(
    null
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [folderReloadKey, setFolderReloadKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [folderErrorMessage, setFolderErrorMessage] = useState<string | null>(
    null
  );
  const activeQueryKeyRef = useRef<string | null>(null);
  const initialRequestIdRef = useRef(0);
  const loadMoreRequestIdRef = useRef(0);
  const loadingCursorRef = useRef<string | null>(null);
  const loadedCursorsRef = useRef(new Set<string>());
  const templateIdsRef = useRef(new Set<string>());
  const activeFolderScopeKeyRef = useRef<string | null>(null);
  const initialFolderRequestIdRef = useRef(0);
  const folderLoadMoreRequestIdRef = useRef(0);
  const loadingFolderCursorRef = useRef<string | null>(null);
  const loadedFolderCursorsRef = useRef(new Set<string>());

  const isSharedOnly = !locationUuid;
  const scope = useMemo(
    () =>
      isSharedOnly
        ? { kind: 'account' as const }
        : { kind: 'profile' as const, locationUuid },
    [isSharedOnly, locationUuid]
  );
  const scopeKey = getOverlayTemplateScopeKey(scope);
  const activeSelection =
    isSharedOnly &&
    selection !== 'shared' &&
    !selection.startsWith('shared-folder:')
      ? 'shared'
      : selection;
  const selectedFolderLibrary: OverlayTemplateFolderLibrary = isSharedOnly
    ? 'shared'
    : getOverlayTemplateFolderLibrary(activeSelection) ?? 'mine';
  const folderScope = scope;
  const shouldLoadFolders = activeSelection !== 'all';
  const folderScopeKey = shouldLoadFolders
    ? `${scopeKey}:${selectedFolderLibrary}`
    : null;
  const selectedFolderId = getOverlayTemplateFolderId(activeSelection);
  const selectionFolderLibrary =
    getOverlayTemplateFolderLibrary(activeSelection);
  const selectedIsShared = isSharedOnly
    ? true
    : selectionFolderLibrary
    ? selectionFolderLibrary === 'shared'
    : activeSelection === 'shared'
    ? true
    : activeSelection === 'mine' || activeSelection.startsWith('folder:')
    ? false
    : undefined;
  const queryKey = JSON.stringify({
    scope: scopeKey,
    isShared: selectedIsShared ?? null,
    folderUuid: selectedFolderId,
    search: searchValue.trim(),
    ordering: orderingBySort[sortValue]
  });
  const selectLibrary = useCallback(
    (nextSelection: OverlayTemplateLibrarySelection) => {
      if (
        !isSharedOnly ||
        nextSelection === 'shared' ||
        nextSelection.startsWith('shared-folder:')
      ) {
        setSelection(nextSelection);
      }
    },
    [isSharedOnly]
  );

  useEffect(() => {
    if (!show || !folderScopeKey) {
      activeFolderScopeKeyRef.current = null;
      setLoadedFolderScopeKey(null);
      folderLoadMoreRequestIdRef.current += 1;
      loadingFolderCursorRef.current = null;
      setFolders([]);
      setNextFoldersUrl(null);
      setIsFolderLoading(false);
      setIsFolderLoadingMore(false);
      setFolderErrorMessage(null);
      setFolderLoadMoreErrorMessage(null);
      return;
    }
    let cancelled = false;
    const requestId = ++initialFolderRequestIdRef.current;
    activeFolderScopeKeyRef.current = folderScopeKey;
    folderLoadMoreRequestIdRef.current += 1;
    loadingFolderCursorRef.current = null;
    loadedFolderCursorsRef.current = new Set();
    setFolders([]);
    setNextFoldersUrl(null);
    setIsFolderLoading(true);
    setIsFolderLoadingMore(false);
    setFolderErrorMessage(null);
    setFolderLoadMoreErrorMessage(null);
    listOverlayTemplateFolders(folderScope, selectedFolderLibrary === 'shared')
      .then((response) => {
        if (
          cancelled ||
          initialFolderRequestIdRef.current !== requestId ||
          activeFolderScopeKeyRef.current !== folderScopeKey
        )
          return;
        setFolders(
          response.results.map((folder) => ({
            id: folder.uuid,
            name: folder.name
          }))
        );
        setNextFoldersUrl(response.next);
      })
      .catch(() => {
        if (
          cancelled ||
          initialFolderRequestIdRef.current !== requestId ||
          activeFolderScopeKeyRef.current !== folderScopeKey
        )
          return;
        setFolderErrorMessage(
          t(
            'lomavisCreator.overlayPicker.folderLoadFailed',
            'Could not load overlay folders.'
          )
        );
      })
      .finally(() => {
        if (
          !cancelled &&
          initialFolderRequestIdRef.current === requestId &&
          activeFolderScopeKeyRef.current === folderScopeKey
        ) {
          setLoadedFolderScopeKey(folderScopeKey);
          setIsFolderLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    folderReloadKey,
    folderScope,
    folderScopeKey,
    selectedFolderLibrary,
    show,
    t
  ]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const requestId = ++initialRequestIdRef.current;
    activeQueryKeyRef.current = queryKey;
    loadMoreRequestIdRef.current += 1;
    loadingCursorRef.current = null;
    loadedCursorsRef.current = new Set();
    templateIdsRef.current = new Set();
    setTemplates([]);
    setTemplateCount(0);
    setNextTemplatesUrl(null);
    setIsLoading(true);
    setIsLoadingMore(false);
    setLoadMoreErrorMessage(null);
    setErrorMessage(null);

    const timeout = window.setTimeout(
      () => {
        listOverlayTemplates({
          scope,
          isShared: selectedIsShared,
          folderUuid: selectedFolderId,
          search: searchValue.trim(),
          ordering: orderingBySort[sortValue]
        })
          .then((response) => {
            if (
              cancelled ||
              initialRequestIdRef.current !== requestId ||
              activeQueryKeyRef.current !== queryKey
            )
              return;
            const initialTemplates = response.results.map((template) => ({
              id: template.uuid,
              title: template.title,
              thumbnailUrl: template.thumbnail_url,
              aspectRatio: template.aspect_ratio,
              folderId: template.folder_uuid,
              isShared: template.is_shared
            }));
            templateIdsRef.current = new Set(
              initialTemplates.map(({ id }) => id)
            );
            setTemplates(initialTemplates);
            setTemplateCount(response.count ?? response.results.length);
            setNextTemplatesUrl(response.next);
          })
          .catch(() => {
            if (
              cancelled ||
              initialRequestIdRef.current !== requestId ||
              activeQueryKeyRef.current !== queryKey
            )
              return;
            setErrorMessage(
              t(
                'lomavisCreator.overlayPicker.loadFailed',
                'Could not load overlays. Please try again.'
              )
            );
          })
          .finally(() => {
            if (
              !cancelled &&
              initialRequestIdRef.current === requestId &&
              activeQueryKeyRef.current === queryKey
            )
              setIsLoading(false);
          });
      },
      searchValue ? TEMPLATE_SEARCH_DEBOUNCE_MS : 0
    );
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    queryKey,
    reloadKey,
    scope,
    searchValue,
    selectedIsShared,
    selectedFolderId,
    show,
    sortValue,
    t
  ]);

  const loadMoreTemplates = useCallback(
    (ignorePreviousError = false) => {
      const nextUrl = nextTemplatesUrl;
      if (
        !show ||
        !nextUrl ||
        isLoading ||
        isLoadingMore ||
        (!ignorePreviousError && loadMoreErrorMessage) ||
        loadingCursorRef.current === nextUrl ||
        loadedCursorsRef.current.has(nextUrl)
      )
        return;

      const requestId = ++loadMoreRequestIdRef.current;
      const requestQueryKey = queryKey;
      loadingCursorRef.current = nextUrl;
      setIsLoadingMore(true);
      setLoadMoreErrorMessage(null);

      listOverlayTemplatesPage(nextUrl)
        .then((response) => {
          if (
            loadMoreRequestIdRef.current !== requestId ||
            activeQueryKeyRef.current !== requestQueryKey ||
            loadingCursorRef.current !== nextUrl
          )
            return;
          const additionalTemplates = response.results
            .filter((template) => {
              if (templateIdsRef.current.has(template.uuid)) return false;
              templateIdsRef.current.add(template.uuid);
              return true;
            })
            .map((template) => ({
              id: template.uuid,
              title: template.title,
              thumbnailUrl: template.thumbnail_url,
              aspectRatio: template.aspect_ratio,
              folderId: template.folder_uuid,
              isShared: template.is_shared
            }));
          setTemplates((current) => [...current, ...additionalTemplates]);
          loadedCursorsRef.current.add(nextUrl);
          setTemplateCount(
            (current) => response.count ?? current + additionalTemplates.length
          );
          setNextTemplatesUrl(
            response.next === nextUrl ||
              (response.next !== null &&
                loadedCursorsRef.current.has(response.next))
              ? null
              : response.next
          );
        })
        .catch(() => {
          if (
            loadMoreRequestIdRef.current !== requestId ||
            activeQueryKeyRef.current !== requestQueryKey ||
            loadingCursorRef.current !== nextUrl
          )
            return;
          setLoadMoreErrorMessage(
            t(
              'lomavisCreator.overlayPicker.loadMoreFailed',
              'Could not load more overlays. Please try again.'
            )
          );
        })
        .finally(() => {
          if (
            loadMoreRequestIdRef.current === requestId &&
            activeQueryKeyRef.current === requestQueryKey &&
            loadingCursorRef.current === nextUrl
          ) {
            loadingCursorRef.current = null;
            setIsLoadingMore(false);
          }
        });
    },
    [
      isLoading,
      isLoadingMore,
      loadMoreErrorMessage,
      nextTemplatesUrl,
      queryKey,
      show,
      t
    ]
  );

  const loadMoreFolders = useCallback(
    (ignorePreviousError = false) => {
      const nextUrl = nextFoldersUrl;
      if (
        !show ||
        !folderScopeKey ||
        !nextUrl ||
        isFolderLoading ||
        isFolderLoadingMore ||
        (!ignorePreviousError && folderLoadMoreErrorMessage) ||
        activeFolderScopeKeyRef.current !== folderScopeKey ||
        loadingFolderCursorRef.current === nextUrl ||
        loadedFolderCursorsRef.current.has(nextUrl)
      )
        return;

      const requestId = ++folderLoadMoreRequestIdRef.current;
      const requestScopeKey = folderScopeKey;
      loadingFolderCursorRef.current = nextUrl;
      setIsFolderLoadingMore(true);
      setFolderLoadMoreErrorMessage(null);

      listOverlayTemplateFolders(
        folderScope,
        selectedFolderLibrary === 'shared',
        nextUrl
      )
        .then((response) => {
          if (
            folderLoadMoreRequestIdRef.current !== requestId ||
            activeFolderScopeKeyRef.current !== requestScopeKey ||
            loadingFolderCursorRef.current !== nextUrl
          )
            return;
          setFolders((current) => {
            const knownIds = new Set(current.map(({ id }) => id));
            const additionalFolders = response.results
              .filter((folder) => {
                if (knownIds.has(folder.uuid)) return false;
                knownIds.add(folder.uuid);
                return true;
              })
              .map((folder) => ({ id: folder.uuid, name: folder.name }));
            return [...current, ...additionalFolders];
          });
          loadedFolderCursorsRef.current.add(nextUrl);
          setNextFoldersUrl(
            response.next === nextUrl ||
              (response.next !== null &&
                loadedFolderCursorsRef.current.has(response.next))
              ? null
              : response.next
          );
        })
        .catch(() => {
          if (
            folderLoadMoreRequestIdRef.current !== requestId ||
            activeFolderScopeKeyRef.current !== requestScopeKey ||
            loadingFolderCursorRef.current !== nextUrl
          )
            return;
          setFolderLoadMoreErrorMessage(
            t(
              'lomavisCreator.overlayPicker.loadMoreFoldersFailed',
              'Could not load more overlay folders. Please try again.'
            )
          );
        })
        .finally(() => {
          if (
            folderLoadMoreRequestIdRef.current === requestId &&
            activeFolderScopeKeyRef.current === requestScopeKey &&
            loadingFolderCursorRef.current === nextUrl
          ) {
            loadingFolderCursorRef.current = null;
            setIsFolderLoadingMore(false);
          }
        });
    },
    [
      folderLoadMoreErrorMessage,
      isFolderLoading,
      isFolderLoadingMore,
      nextFoldersUrl,
      folderScope,
      folderScopeKey,
      selectedFolderLibrary,
      show,
      t
    ]
  );

  const selectTemplate = async (templateId: string) => {
    setSelectingTemplateId(templateId);
    try {
      const template = await getOverlayTemplate(templateId, scope);
      onSelect(template);
      onClose();
    } catch {
      setErrorMessage(
        t(
          'lomavisCreator.overlayPicker.selectFailed',
          'Could not load the selected overlay. Please try again.'
        )
      );
    } finally {
      setSelectingTemplateId(null);
    }
  };

  return (
    <Modal
      centered
      show={show}
      onHide={onClose}
      size="xl"
      dialogAs={WideModalDialog}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t('lomavisCreator.overlayPicker.title', 'Choose an overlay')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="p-0 overflow-hidden"
        style={{ height: TEMPLATE_SELECTOR_BODY_HEIGHT }}
      >
        <LomavisOverlayTemplateLibrary
          mode={isSharedOnly ? 'accountPicker' : 'profileGroup'}
          templates={templates}
          folders={folders}
          selection={activeSelection}
          folderLibrary={selectedFolderLibrary}
          showAllCategory={!isSharedOnly}
          showSharedCategory
          searchValue={searchValue}
          sortValue={sortValue}
          sortOptions={[
            {
              value: 'newest',
              label: t('overlayTemplates.sortNewest', 'Newest first')
            },
            {
              value: 'oldest',
              label: t('overlayTemplates.sortOldest', 'Oldest first')
            },
            {
              value: 'nameAsc',
              label: t('overlayTemplates.sortNameAsc', 'Name A–Z')
            },
            {
              value: 'nameDesc',
              label: t('overlayTemplates.sortNameDesc', 'Name Z–A')
            }
          ]}
          labels={{
            title: t('overlayTemplates.profile.overlaysTitle', 'Overlays'),
            description: t(
              'overlayTemplates.library.overlayPickerDescription',
              'Choose an overlay for your post.'
            ),
            back: t('common.back', 'Back'),
            createTemplate: t(
              'overlayTemplates.createOverlay',
              'Create overlay'
            ),
            addFromAccountLibrary: t(
              'overlayTemplates.addFromAccountLibrary',
              'Add from account library'
            ),
            search: t('overlayTemplates.searchOverlays', 'Search overlays'),
            sort: t('overlayTemplates.sortOverlays', 'Sort overlays'),
            emptyTitle: t(
              'overlayTemplates.emptyOverlaysFound',
              'No overlays found'
            ),
            emptyDescription: t(
              'overlayTemplates.emptyOverlaysSearchDescription',
              'Try a different folder or search.'
            ),
            retry: t('common.retry', 'Retry'),
            loading: t('common.loading', 'Loading…'),
            templateCount: (count) =>
              t('overlayTemplates.overlayCount', '{count} overlays', { count }),
            folderSidebar: {
              navigation: t(
                'overlayTemplates.overlayFolderNavigation',
                'Overlay folders'
              ),
              title: t('overlayTemplates.folders', 'Folders'),
              allTemplates: t('overlayTemplates.allOverlays', 'All overlays'),
              sharedTemplates: t(
                'overlayTemplates.sharedOverlays',
                'Shared overlays'
              ),
              myTemplates: t('overlayTemplates.myOverlays', 'My overlays'),
              newFolder: t('overlayTemplates.createFolder', 'Create folder'),
              folderActions: t(
                'overlayTemplates.folderActions',
                'Folder actions'
              ),
              renameFolder: t('overlayTemplates.renameFolder', 'Rename folder'),
              deleteFolder: t('overlayTemplates.deleteFolder', 'Delete folder')
            },
            card: {
              shared: t('overlayTemplates.shared', 'Shared'),
              sharedOverlay: t(
                'overlayTemplates.sharedOverlay',
                'Shared overlay'
              ),
              edit: t('common.edit', 'Edit'),
              changeFolder: t('overlayTemplates.changeFolder', 'Change folder'),
              duplicate: t('overlayTemplates.duplicate', 'Duplicate'),
              delete: t('common.delete', 'Delete'),
              startPost: selectingTemplateId
                ? t('common.loading', 'Loading…')
                : t('overlayTemplates.selectOverlay', 'Select overlay'),
              thumbnailLoading: t('common.loading', 'Loading…'),
              thumbnailUnavailable: t(
                'overlayTemplates.thumbnailUnavailable',
                'Preview unavailable'
              )
            }
          }}
          showPageHeading={false}
          templateCount={templateCount}
          isLoading={isLoading || selectingTemplateId !== null}
          hasMoreTemplates={Boolean(nextTemplatesUrl)}
          isLoadingMore={isLoadingMore}
          isFolderLoading={
            Boolean(folderScopeKey) &&
            (isFolderLoading || loadedFolderScopeKey !== folderScopeKey)
          }
          hasMoreFolders={Boolean(folderScopeKey && nextFoldersUrl)}
          isFolderLoadingMore={isFolderLoadingMore}
          errorMessage={errorMessage}
          loadMoreErrorMessage={loadMoreErrorMessage}
          folderErrorMessage={folderErrorMessage}
          folderLoadMoreErrorMessage={folderLoadMoreErrorMessage}
          onSearchChange={setSearchValue}
          onSortChange={setSortValue}
          onSelectionChange={selectLibrary}
          onStartPost={(templateId) => {
            void selectTemplate(templateId);
          }}
          onLoadMore={loadMoreTemplates}
          onLoadMoreFolders={loadMoreFolders}
          onRetryLoadMore={() => loadMoreTemplates(true)}
          onRetryFolders={() => setFolderReloadKey((key) => key + 1)}
          onRetryLoadMoreFolders={() => loadMoreFolders(true)}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      </Modal.Body>
    </Modal>
  );
};

export default OverlayTemplateSelectorModal;
